import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Check, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rr_panel_token";

const panelApi = axios.create({ baseURL: API });
panelApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function Panel() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setChecking(false); return; }
    panelApi.get("/panel/me").then(() => setAuthed(true)).catch(() => localStorage.removeItem(TOKEN_KEY)).finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0F0F0F" }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: "#B08D57" }} /></div>;
  return authed ? <PanelShell onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} /> : <PanelLogin onLogin={() => setAuthed(true)} />;
}

function PanelShell({ onLogout }) {
  const [tab, setTab] = useState("packages");
  return (
    <div className="min-h-screen flex" style={{ background: "#0F0F0F", color: "#F3EEE4", fontFamily: "Manrope, sans-serif" }} data-testid="panel-editor">
      <aside className="w-60 shrink-0 flex flex-col" style={{ borderRight: "1px solid #2A2A2A" }}>
        <div className="px-6 py-6" style={{ borderBottom: "1px solid #2A2A2A" }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} className="text-2xl">ROKERT Console</h1>
          <p className="text-[11px] mt-1" style={{ color: "#8A968F" }}>Restricted operations access</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem active={tab === "packages"} onClick={() => setTab("packages")} testid="panel-nav-packages">Packages & Pricing</NavItem>
          <NavItem active={tab === "customers"} onClick={() => setTab("customers")} testid="panel-nav-customers">Customers</NavItem>
        </nav>
        <div className="p-3" style={{ borderTop: "1px solid #2A2A2A" }}>
          <button onClick={onLogout} data-testid="panel-logout"
            className="w-full flex items-center gap-2 text-sm px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: "#8A968F" }}>
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        {tab === "packages" ? <PriceEditor /> : <CustomersPanel />}
      </main>
    </div>
  );
}

function NavItem({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors"
      style={{ background: active ? "rgba(176,141,87,0.12)" : "transparent", color: active ? "#B08D57" : "#C9C9C9" }}>
      {children}
    </button>
  );
}

function PanelLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const r = await panelApi.post("/panel/login", { username, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin();
    } catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0F0F0F", fontFamily: "Manrope, sans-serif" }} data-testid="panel-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "rgba(176,141,87,0.15)" }}><Lock className="w-6 h-6" style={{ color: "#B08D57" }} /></div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: "#F3EEE4" }} className="text-4xl mt-5">ROKERT Console</h1>
          <p className="text-sm mt-2" style={{ color: "#8A968F" }}>Rokert Retreats · restricted access</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" data-testid="panel-username"
            className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={{ background: "#181818", border: "1px solid #2A2A2A", color: "#F3EEE4" }} />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="panel-password"
            className="w-full rounded-lg px-4 py-3 text-sm outline-none" style={{ background: "#181818", border: "1px solid #2A2A2A", color: "#F3EEE4" }} />
          {err && <p className="text-sm" style={{ color: "#E9807F" }} data-testid="panel-error">{err}</p>}
          <button type="submit" disabled={loading} data-testid="panel-login-btn"
            className="w-full py-3.5 rounded-lg font-semibold text-sm uppercase tracking-wide flex items-center justify-center gap-2" style={{ background: "#B08D57", color: "#0F0F0F" }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PriceEditor() {
  const [packages, setPackages] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => panelApi.get("/panel/packages").then((r) => setPackages(r.data)).catch(() => setPackages([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="px-6 md:px-10 py-8" data-testid="panel-packages-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} className="text-3xl">Packages & Pricing</h2>
          <p className="text-xs mt-1" style={{ color: "#8A968F" }}>Update name, location, photo, price & availability — changes are live immediately</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="panel-add-destination-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs uppercase tracking-wide font-semibold shrink-0" style={{ background: "#B08D57", color: "#0F0F0F" }}>
          <Plus className="w-4 h-4" /> Add Destination
        </button>
      </div>
      <div className="max-w-3xl">
        {packages === null ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B08D57" }} /></div>
          : <div className="space-y-5">{packages.map((p) => <PackageRow key={p.slug} pkg={p} onSaved={load} />)}</div>}
      </div>
      {showAdd && <AddDestinationModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddDestinationModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", area: "", tagline: "", hero_image: "", price_per_night_eur: "", guests_max: "6" });
  const [saving, setSaving] = useState(false);
  const field = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.area.trim() || !form.tagline.trim() || !form.hero_image.trim() || !form.price_per_night_eur) {
      return toast.error("Please fill in name, location, tagline, photo & price");
    }
    setSaving(true);
    try {
      await panelApi.post("/panel/packages", {
        name: form.name, area: form.area, tagline: form.tagline, hero_image: form.hero_image,
        price_per_night_eur: parseFloat(form.price_per_night_eur), guests_max: parseInt(form.guests_max, 10) || 6,
      });
      toast.success(`${form.name} added — now live in the destination list`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add destination"); }
    finally { setSaving(false); }
  };

  const inputStyle = { background: "#0F0F0F", border: "1px solid #2A2A2A", color: "#F3EEE4" };
  const labelStyle = { color: "#8A968F" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="panel-add-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl p-6" style={{ background: "#181818", border: "1px solid #2A2A2A" }}>
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} className="text-2xl">New Destination</h2>
          <button onClick={onClose} data-testid="panel-add-close" style={{ color: "#8A968F" }}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Name</label>
            <input value={form.name} onChange={(e) => field("name", e.target.value)} placeholder="e.g. The Serenity of Ella" data-testid="panel-add-name"
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Location</label>
            <input value={form.area} onChange={(e) => field("area", e.target.value)} placeholder="e.g. Ella" data-testid="panel-add-area"
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Tagline</label>
            <input value={form.tagline} onChange={(e) => field("tagline", e.target.value)} placeholder="Short one-line description" data-testid="panel-add-tagline"
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Photo URL</label>
            <input value={form.hero_image} onChange={(e) => field("hero_image", e.target.value)} placeholder="https://..." data-testid="panel-add-photo"
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Price / night (€)</label>
              <input type="number" value={form.price_per_night_eur} onChange={(e) => field("price_per_night_eur", e.target.value)} data-testid="panel-add-price"
                className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Max guests</label>
              <input type="number" value={form.guests_max} onChange={(e) => field("guests_max", e.target.value)} data-testid="panel-add-guests"
                className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
            </div>
          </div>
        </div>
        <button onClick={submit} disabled={saving} data-testid="panel-add-submit"
          className="mt-5 w-full py-3 rounded-lg text-xs uppercase tracking-wide font-semibold flex items-center justify-center gap-2" style={{ background: "#B08D57", color: "#0F0F0F" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Destination"}
        </button>
      </div>
    </div>
  );
}

function PackageRow({ pkg, onSaved }) {
  const [form, setForm] = useState({ name: pkg.name, area: pkg.area, hero_image: pkg.hero_image, price_per_night_eur: pkg.price_per_night_eur });
  const [bookable, setBookable] = useState(pkg.bookable !== false);
  const [earlyBirdOn, setEarlyBirdOn] = useState((pkg.early_bird_discount_percent || 0) > 0);
  const [earlyBirdPct, setEarlyBirdPct] = useState(pkg.early_bird_discount_percent || 10);
  const [savingPct, setSavingPct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toggling, setToggling] = useState(false);

  const field = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await panelApi.put(`/panel/packages/${pkg.slug}`, {
        name: form.name, area: form.area, hero_image: form.hero_image,
        price_per_night_eur: parseFloat(form.price_per_night_eur),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch {} finally { setSaving(false); }
  };

  const toggleBookable = async () => {
    const nextVal = !bookable;
    setToggling(true);
    setBookable(nextVal);
    try {
      await panelApi.put(`/panel/packages/${pkg.slug}`, { bookable: nextVal });
      toast.success(nextVal ? `${pkg.name} is now bookable` : `${pkg.name} hidden from booking`);
      onSaved();
    } catch { toast.error("Could not update availability"); setBookable(!nextVal); }
    finally { setToggling(false); }
  };

  const toggleEarlyBird = async () => {
    const nextOn = !earlyBirdOn;
    const nextPct = nextOn ? (earlyBirdPct || 10) : 0;
    setEarlyBirdOn(nextOn);
    if (nextOn) setEarlyBirdPct(nextPct);
    try {
      await panelApi.put(`/panel/packages/${pkg.slug}`, { early_bird_discount_percent: nextPct });
      toast.success(nextOn ? `Early bird (${nextPct}%) active on ${pkg.name}` : `Early bird removed from ${pkg.name}`);
      onSaved();
    } catch { toast.error("Could not update early bird discount"); setEarlyBirdOn(!nextOn); }
  };

  const applyEarlyBirdPct = async () => {
    const pct = parseFloat(earlyBirdPct);
    if (isNaN(pct) || pct < 0 || pct > 100) return toast.error("Enter a discount between 0 and 100");
    setSavingPct(true);
    try {
      await panelApi.put(`/panel/packages/${pkg.slug}`, { early_bird_discount_percent: pct });
      toast.success(`Early bird discount set to ${pct}% on ${pkg.name}`);
      onSaved();
    } catch { toast.error("Could not update early bird discount"); }
    finally { setSavingPct(false); }
  };

  const inputStyle = { background: "#0F0F0F", border: "1px solid #2A2A2A", color: "#F3EEE4" };
  const labelStyle = { color: "#8A968F" };

  return (
    <div className="rounded-xl p-5" style={{ background: "#181818", border: "1px solid #2A2A2A" }} data-testid={`panel-row-${pkg.slug}`}>
      <div className="flex gap-5">
        <img src={form.hero_image} alt="" className="w-28 h-28 rounded-lg object-cover shrink-0" />
        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Name</label>
            <input value={form.name} onChange={(e) => field("name", e.target.value)} data-testid={`panel-name-${pkg.slug}`}
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Location</label>
            <input value={form.area} onChange={(e) => field("area", e.target.value)} data-testid={`panel-area-${pkg.slug}`}
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Price / night (€)</label>
            <input type="number" value={form.price_per_night_eur} onChange={(e) => field("price_per_night_eur", e.target.value)} data-testid={`panel-price-${pkg.slug}`}
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={labelStyle}>Photo URL</label>
            <input value={form.hero_image} onChange={(e) => field("hero_image", e.target.value)} data-testid={`panel-photo-${pkg.slug}`}
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none" style={inputStyle} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg px-4 py-3 mt-4" style={{ background: bookable ? "rgba(176,141,87,0.08)" : "#0F0F0F", border: `1px solid ${bookable ? "#B08D57" : "#2A2A2A"}` }}>
        <div>
          <p className="text-sm font-medium">Available as a booking destination</p>
          <p className="text-[11px]" style={{ color: "#8A968F" }}>{bookable ? "Shown to guests, selectable" : "Visible with a 'Not Available' ribbon, not selectable"}</p>
        </div>
        <button onClick={toggleBookable} disabled={toggling} data-testid={`panel-bookable-${pkg.slug}`} aria-pressed={bookable}
          className="relative w-12 h-7 rounded-full transition-colors shrink-0" style={{ background: bookable ? "#B08D57" : "#3A3A3A" }}>
          <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: bookable ? "translateX(20px)" : "translateX(0)" }} />
        </button>
      </div>

      <div className="rounded-lg px-4 py-3 mt-3" style={{ background: earlyBirdOn ? "rgba(176,141,87,0.08)" : "#0F0F0F", border: `1px solid ${earlyBirdOn ? "#B08D57" : "#2A2A2A"}` }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Early Bird discount</p>
            <p className="text-[11px]" style={{ color: "#8A968F" }}>{earlyBirdOn ? `Applied to the total on every booking type` : "Off — guests see the usual price"}</p>
          </div>
          <button onClick={toggleEarlyBird} data-testid={`panel-earlybird-${pkg.slug}`} aria-pressed={earlyBirdOn}
            className="relative w-12 h-7 rounded-full transition-colors shrink-0" style={{ background: earlyBirdOn ? "#B08D57" : "#3A3A3A" }}>
            <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: earlyBirdOn ? "translateX(20px)" : "translateX(0)" }} />
          </button>
        </div>
        {earlyBirdOn && (
          <div className="flex items-center gap-2 mt-3">
            <input type="number" min="0" max="100" value={earlyBirdPct} onChange={(e) => setEarlyBirdPct(e.target.value)}
              data-testid={`panel-earlybird-pct-${pkg.slug}`} className="w-24 rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle} />
            <span className="text-sm" style={{ color: "#8A968F" }}>% off the total</span>
            <button onClick={applyEarlyBirdPct} disabled={savingPct} data-testid={`panel-earlybird-apply-${pkg.slug}`}
              className="ml-auto px-4 py-2 rounded-lg text-xs uppercase tracking-wide font-semibold" style={{ background: "#B08D57", color: "#0F0F0F" }}>
              {savingPct ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={saving} data-testid={`panel-save-${pkg.slug}`}
          className="px-6 py-2.5 rounded-lg text-xs uppercase tracking-wide font-semibold flex items-center gap-2" style={{ background: saved ? "#173229" : "#B08D57", color: saved ? "#F3EEE4" : "#0F0F0F" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><Check className="w-4 h-4" /> Saved</> : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: "requested", label: "Requested" },
  { value: "availability_confirmed", label: "Availability Confirmed" },
  { value: "waiting_for_payment", label: "Waiting for Payment" },
];

function CustomersPanel() {
  const [customers, setCustomers] = useState(null);

  const load = () => panelApi.get("/panel/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (c, status) => {
    try {
      const path = c.type === "booking" ? `/panel/bookings/${c.id}/status` : `/panel/leads/${c.id}/status`;
      await panelApi.put(path, { status });
      toast.success(`${c.name || "Customer"} marked as ${STATUS_OPTIONS.find((s) => s.value === status)?.label} — guest notified by email`);
      load();
    } catch { toast.error("Could not update status"); }
  };

  if (customers === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B08D57" }} /></div>;

  return (
    <div className="px-6 md:px-10 py-8" data-testid="panel-customers">
      <div className="mb-8">
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }} className="text-3xl">Customers</h2>
        <p className="text-xs mt-1" style={{ color: "#8A968F" }}>ROKERT 14 bookings & ROKERT CUSTOM enquiries that have sent a booking request. Changing status emails the guest automatically.</p>
      </div>
      {customers.length === 0 ? (
        <p className="text-sm" style={{ color: "#8A968F" }}>No booking requests yet.</p>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #2A2A2A" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left" style={{ background: "#181818", color: "#8A968F" }}>
                <th className="px-4 py-3 font-medium">Guest</th>
                <th className="px-4 py-3 font-medium">Journey</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={`${c.type}-${c.id}`} data-testid={`panel-customer-${c.id}`} style={{ borderTop: "1px solid #2A2A2A" }}>
                  <td className="px-4 py-3">
                    <p>{c.name || "—"}</p>
                    <p className="text-[11px]" style={{ color: "#8A968F" }}>{c.email || "—"}</p>
                  </td>
                  <td className="px-4 py-3">{c.journey}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ background: c.type === "booking" ? "rgba(176,141,87,0.15)" : "rgba(138,150,143,0.18)", color: c.type === "booking" ? "#B08D57" : "#C9C9C9" }}>
                      {c.type === "booking" ? "ROKERT 14" : "ROKERT CUSTOM"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{c.total ? `€${Math.round(c.total).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3">
                    <select value={c.status} onChange={(e) => setStatus(c, e.target.value)} data-testid={`panel-customer-status-${c.id}`}
                      className="rounded-lg px-2.5 py-1.5 text-xs outline-none" style={{ background: "#0F0F0F", border: "1px solid #2A2A2A", color: "#F3EEE4" }}>
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
